import { Button, Group } from '@common/ui'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { com } from '@eidu/web'
import EntityFieldDisplayGroup from '../shared/EntityFieldDisplayGroup'
import PageLayout from '../shared/PageLayout'
import { KindViewPageProps } from '../KindPages'
import LoginIdentifierField from '../../../../components/input/LoginIdentifierField'
import { useImpersonation } from '../../../../io/context/ImpersonationContext'
import { extractProfileClaims } from '../../../../io/api/authorization/profileClaims'
import emailLoginIdentifierKey = com.eidu.entity.emailLoginIdentifierKey
import phoneNumberLoginIdentifierKey = com.eidu.entity.phoneNumberLoginIdentifierKey
import EntityAction = com.eidu.entity.permission.EntityAction
import useHasPermission from '../../../permission/useHasPermission'
import useHasGlobalPermissions from '../../../permission/useHasGlobalPermissions'
import GlobalAction = com.eidu.entity.permission.GlobalAction

const UserViewPage = ({ entity, entityType, fields }: KindViewPageProps) => {
  const navigate = useNavigate()
  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/edit/${entity.entity.id.toString()}`)
  }

  const externalValues = entity.entity.externalValues?.asJsReadonlyMapView() ?? new Map()
  const phone = externalValues.get(phoneNumberLoginIdentifierKey) || ''
  const email = externalValues.get(emailLoginIdentifierKey) || ''

  const { hasPermission: hasUpdatePermission } = useHasPermission(entity.entity.id, EntityAction.UpdateEntity)

  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManageAuthentication, GlobalAction.Impersonate)
  const showAuthenticationInfo = hasPermission(GlobalAction.ManageAuthentication)
  const canImpersonate = hasPermission(GlobalAction.Impersonate)

  const { isReadOnly, startImpersonation } = useImpersonation()
  const auth = useAuth()
  const currentUserEntityId = auth.user?.profile ? extractProfileClaims(auth.user.profile).userEntityId : undefined
  const isSelf = currentUserEntityId === entity.entity.id.toString()
  const showImpersonate = canImpersonate && !isSelf

  const handleImpersonate = () => {
    startImpersonation(entity.entity.id.toString())
    navigate('/')
  }

  const editButton =
    hasUpdatePermission && !isReadOnly ? (
      <Button variant="subtle" onClick={navigateToEdit} data-testid="entity-action-edit">
        Edit
      </Button>
    ) : undefined

  const impersonateButton = showImpersonate ? (
    <Button onClick={handleImpersonate} data-testid="entity-action-impersonate" variant="outline">
      Impersonate
    </Button>
  ) : undefined

  const actions =
    editButton || impersonateButton ? (
      <Group gap="xs">
        {impersonateButton}
        {editButton}
      </Group>
    ) : undefined

  return (
    <PageLayout
      title={entity.primaryLabelText}
      subtitle={entityType.name}
      auditTargetId={entity.entity.id.toString()}
      actions={actions}
      showBack
    >
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: 12 }}>
        <EntityFieldDisplayGroup entityType={entityType} fields={fields} />
        {showAuthenticationInfo && <LoginIdentifierField phone={phone} email={email} disabled />}
      </div>
    </PageLayout>
  )
}

export default UserViewPage
